import React from 'react';
import {
    Box,
    Button,
    HStack,
    Icon,
    IconButton,
    Input,
    Switch,
    Text,
    VStack
} from "@chakra-ui/react";
import {useForm} from "@/uses/useForm";
import {useAddField} from "@/uses/useAddField";
import stringUtil from "@/utils/stringUtil";
import ConditionalForm from "@/components/form/ConditionalForm";
import {useModal} from "@/contexts/modalContext";
import {CircleAlert, X} from "lucide-react";
import {Field, FilesInput, LanguageField} from '@/components';
import {useLanguage} from "@/contexts/languageContext";
import objectUtil from "@/utils/objectUtil";

export const AddChoice = ({fields, type}) => {

    const {getLangObject, t, language} = useLanguage();

    const {
        formData: currentFormData,
        formFieldErrors: currentFormFieldErrors,
        handleInputChange: currentHandleInputChange,
        handleFormSubmit: currentHandleFormSubmit
    } = useForm(
        {
            choice: getLangObject(),
        },
        {
            choice: {
                custom: {
                    function: (value) => {
                        return Object.values(value).every((value) => value && value.length >= 3)
                    },
                    message: t('errors.pleaseFillFieldAllLanguages')
                },
            },
        }
    );

    const {open, close} = useModal();
    const {formData, formFieldErrors, handleInputChange, handleFormSubmit} = useForm(
        {
            id: stringUtil.generateAlphanumericHash(8),
            type: type,
            choices: getLangObject([]),
            label: getLangObject(),
            images: [],
            required: false,
            conditional: null,
        },
        {
            label: {
                custom: {
                    function: (value) => {
                        return Object.values(value).every((value) => value && value.length >= 3)
                    },
                    message: t('errors.pleaseFillFieldAllLanguages')
                },
            },
            choices: {
                custom: {
                    function: (value) => {
                        return Object.values(value).every((value) => value && value.length >= 2)
                    },
                    message: t('errors.addAtLeastChoices', {value: 2})
                }
            },
        }
    );

    useAddField(handleFormSubmit, formData);

    return (
        <VStack w="100%" flex={1} overflowY="auto" alignItems="stretch" p={4}>
            <VStack alignItems="flex-start" gap="2vh">
                <HStack w="100%" justifyContent="space-between" alignItems="center">
                    <Field
                        label={t('wrappers.nonMandatoryLabel', { value: t('misc.mandatoryField') })}
                        isDisabled={formData?.conditional}
                        display="flex"
                        alignItems="center"
                        mb="2vh"
                    >
                        <Switch.Root
                            size="lg"
                            id={`required-field-${type}`}
                            checked={formData.required}
                            onCheckedChange={(e) => handleInputChange('required', Boolean(e?.checked))}
                        >
                            <Switch.HiddenInput/>
                            <Switch.Control/>
                            <Switch.Label/>
                        </Switch.Root>
                    </Field>
                    <Field
                        label={t('wrappers.nonMandatoryLabel', { value: t('fields.condition') })}
                        display="flex"
                        alignItems="center"
                        isDisabled={formData?.required === true || fields.length === 0}
                        mb="2vh"
                    >
                        <Switch.Root
                            size="lg"
                            id={`required-field-${type}`}
                            checked={formData.conditional !== null}
                            onCheckedChange={(e) => {
                                if (Boolean(e?.checked)) {
                                    open(<ConditionalForm close={() => close('addFormField')}
                                                          onSubmit={(data) => handleInputChange('conditional', data)}
                                                          fields={fields}/>, '3xl', {closeOnOverlayClick: false}, true, 'conditionalModal')
                                } else {
                                    handleInputChange('conditional', null)
                                }
                            }}
                        >
                            <Switch.HiddenInput/>
                            <Switch.Control/>
                            <Switch.Label/>
                        </Switch.Root>
                    </Field>
                </HStack>
                <HStack w="100%" align="flex-start" justify="space-between">
                    <LanguageField
                        id="label"
                        overflow="hidden"
                        errors={formFieldErrors.label}
                        label={t('wrappers.mandatoryLabel', { value: t('fields.label') })}
                        renderElement={(lang) => <Input
                            value={formData.label[lang]}
                            onChange={(e) => handleInputChange('label', (prevData) => ({
                                ...prevData.label,
                                [lang]: e?.target?.value
                            }))}
                            placeholder={t('fields.label')}
                        />}
                    />
                </HStack>
                <Field
                    w="100%"
                    label={t('wrappers.nonMandatoryLabel', { value: t('objects.image_other') })}
                >
                    <FilesInput
                        w="100%"
                        noLabel
                        multiple
                        accept="image/*"
                        files={formData.images ?? []}
                        onChange={(images) => handleInputChange('images', images)}
                    />
                </Field>
                <VStack
                    alignItems="stretch"
                    borderRadius="xl"
                    borderColor="red.500"
                    mt="1rem"
                    w="100%"
                    {...(formFieldErrors.choices ? {borderWidth: '1px',} : {})}
                >
                    <HStack alignItems="flex-end" gap="1rem">
                        <LanguageField
                            id="choice"
                            flex={1}
                            overflow="hidden"
                            errors={currentFormFieldErrors.choice}
                            label={t('wrappers.mandatoryLabel', { value: t('sentences.addAChoice') })}
                            renderElement={(lang) => <Input
                                value={currentFormData.choice[lang]}
                                onChange={(e) => currentHandleInputChange('choice', (prevData) => ({
                                    ...prevData.choice,
                                    [lang]: e?.target?.value
                                }))}
                                placeholder={t('fields.choice')}
                            />}
                        />
                        <Button
                            borderRadius=".5rem"
                            style={{padding: "1.5rem 2.5rem"}}
                            onClick={() => currentHandleFormSubmit(async (data) => {
                                if (Object.keys(formData.choices).every((lang) => !formData.choices[lang].includes(data.choice[lang]))) {
                                    handleInputChange('choices', Object.fromEntries(
                                        Object.entries(formData.choices).map(([lang, value]) => ([
                                            lang,
                                            [...value, data.choice[lang]]
                                        ]))
                                    ));
                                }
                                currentHandleInputChange('choice', getLangObject([]));
                            }, () => null)}
                        >
                            {t('sentences.addTheChoice')}
                        </Button>
                    </HStack>
                    {
                        formFieldErrors.choices && <HStack color="red.500" gap={0}>
                            <Icon as={CircleAlert} boxSize={6}/>
                            <Text fontSize="sm">{formFieldErrors.choices}</Text>
                        </HStack>
                    }
                </VStack>
            </VStack>
            <VStack flex={1} p={4} w="100%" alignItems="flex-start">
                {formData.choices[language].length > 0
                    ? formData.choices[language].map((e, i) => (
                        <HStack key={`choice-${e}`} alignItems="center" gap="1rem">
                            <Box w="100%">● {e}</Box>
                            <IconButton
                                aria-label={t('sentences.deleteChoice')}
                                icon={<X/>}
                                onClick={() => {
                                    handleInputChange('choices', (prevState) => {
                                        Object.fromEntries(Object.entries(prevState).map(([lang, value]) => {
                                            return [lang, objectUtil.deleteObjectByIndex(value, i)];
                                        }));
                                    });
                                }}
                                isRound={true}
                                variant="iconOnly"
                                boxSize={10}
                            />
                        </HStack>
                    ))
                    : <Box w="100%">{t('sentences.noChoice')}</Box>

                }
            </VStack>
        </VStack>
    );
};

export default AddChoice;
